import React, { useEffect, useState } from 'react';
import { IconX } from '@tabler/icons-react';
import { getSubscriptionTimeline } from '../../services/esetApi';
import type { EsetTimelineEvent } from '../../services/esetApi';
import './EsetTimelinePanel.css';

interface EsetTimelinePanelProps {
  /** The subscription (admin-table row) whose change history to show; null = closed. */
  subscriptionId: string | null;
  customerName: string | null;
  onClose: () => void;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso.slice(0, 10)
    : d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Timeline dot style: hollow amber for a (synthetic) expiry marker, green for a payment,
 *  grey for trial events, blue otherwise. */
function dotModifier(ev: EsetTimelineEvent): string {
  if (ev.type === 'subscription_expiry') return ' eset-tl__dot--expiry';
  if (ev.type === 'payment_received') return ' eset-tl__dot--payment';
  if (ev.is_trial) return ' eset-tl__dot--trial';
  return '';
}

export const EsetTimelinePanel: React.FC<EsetTimelinePanelProps> = ({
  subscriptionId,
  customerName,
  onClose,
}) => {
  const [events, setEvents] = useState<EsetTimelineEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTrials, setShowTrials] = useState(false);
  const [showExpiry, setShowExpiry] = useState(false);

  useEffect(() => {
    if (!subscriptionId) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    getSubscriptionTimeline(subscriptionId, showTrials, showExpiry, controller.signal)
      .then((t) => setEvents(t.events ?? []))
      .catch((e) => {
        if ((e as DOMException)?.name === 'AbortError') return;
        setError(e instanceof Error ? e.message : 'Failed to load timeline');
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [subscriptionId, showTrials, showExpiry]);

  if (!subscriptionId) return null;

  return (
    <div className="eset-tl-overlay" role="dialog" aria-label="ESET change timeline">
      <div className="eset-tl-overlay__scrim" onClick={onClose} />
      <div className="eset-tl-panel">
        <div className="eset-tl__header">
          <span className="eset-tl__title">
            Change timeline
            {customerName && <span className="eset-tl__subtitle">{customerName}</span>}
          </span>
          <button className="eset-tl__close" onClick={onClose} aria-label="Close">
            <IconX size={16} stroke={1.5} />
          </button>
        </div>

        <div className="eset-tl__toolbar">
          <label className="eset-tl__toggle">
            <input
              type="checkbox"
              checked={showTrials}
              onChange={(e) => setShowTrials(e.target.checked)}
            />
            Show trial events
          </label>
          <label className="eset-tl__toggle">
            <input
              type="checkbox"
              checked={showExpiry}
              onChange={(e) => setShowExpiry(e.target.checked)}
            />
            Show subscription expiry dates
          </label>
        </div>

        <div className="eset-tl__body">
          {loading && <div className="eset-tl__muted">Loading…</div>}
          {error && <div className="eset-tl__error">{error}</div>}
          {!loading && !error && events.length === 0 && (
            <div className="eset-tl__muted">No events yet.</div>
          )}
          {!loading && !error && events.length > 0 && (
            <ol className="eset-tl__list">
              {events.map((ev, i) => (
                <li key={`${ev.date}-${ev.type}-${i}`} className="eset-tl__event">
                  <div className="eset-tl__date">{formatDate(ev.date)}</div>
                  <div className={`eset-tl__dot${dotModifier(ev)}`} />
                  <div className="eset-tl__content">
                    <div className="eset-tl__summary">
                      {ev.summary}
                      {ev.is_trial && <span className="eset-tl__badge">trial</span>}
                      {ev.source === 'seed' && <span className="eset-tl__badge eset-tl__badge--seed">derived</span>}
                    </div>
                    {ev.detail && <div className="eset-tl__detail">{ev.detail}</div>}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
};
